import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Smartphone, 
  CalendarDays, 
  Calculator, 
  UserCheck, 
  ArrowRight, 
  Star, 
  CheckCircle2, 
  Globe, 
  Zap, 
  Leaf,
  Monitor,
  Play,
  TrendingUp,
  Users,
  Shield,
  Clock,
  BarChart3,
  Award,
  MessageCircle
} from 'lucide-react';

// Rutas a imágenes en public/images/
const dx1 = '/images/dx1.png';
const dx2 = '/images/dx2.png';
const dx3 = '/images/dx3.png';
const dx4 = '/images/dx4.png';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  const platformSlides = [
    {
      url: dx1,
      title: "Catálogo Digital Actualizado",
      desc: "Visualiza todos los productos DXN con información actualizada directamente desde los Centros de Servicio en Mi DXN.",
      icon: Globe,
      badge: "Tiempo Real",
      badgeColor: "bg-emerald-500"
    },
    {
      url: dx2,
      title: "Gestión de Eventos",
      desc: "Organiza, programa y da seguimiento a tus reuniones de equipo y presentaciones desde Mi DXN.",
      icon: CalendarDays,
      badge: "Organización",
      badgeColor: "bg-amber-500"
    },
    {
      url: dx3,
      title: "Calculadora de PV y SV",
      desc: "Planifica tus compras y conoce al instante los Puntos de Volumen y Valor para alcanzar tus rangos.",
      icon: Calculator,
      badge: "Estrategia",
      badgeColor: "bg-teal-500"
    },
    {
      url: dx4,
      title: "Verificador de DNI",
      desc: "Consulta en segundos si un posible socio ya está afiliado. Evita duplicidades y ahorra tiempo valioso.",
      icon: UserCheck,
      badge: "Eficiencia",
      badgeColor: "bg-rose-500"
    }
  ];

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % platformSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isPlaying]);

  const stats = [
    { icon: Users, value: "500+", label: "Distribuidores Activos", color: "text-emerald-600" },
    { icon: BarChart3, value: "50%", label: "Más Productividad", color: "text-amber-600" },
    { icon: Clock, value: "24/7", label: "Disponibilidad", color: "text-teal-600" },
    { icon: Shield, value: "100%", label: "Seguridad de Datos", color: "text-rose-600" }
  ];

  const features = [
    { 
      icon: Smartphone, 
      title: 'App Mi DXN Móvil', 
      desc: 'Lleva tu negocio en el bolsillo. Muestra el catálogo y gestiona tu equipo desde cualquier lugar.',
      color: 'emerald'
    },
    { 
      icon: Monitor, 
      title: 'Sistema Web Mi DXN', 
      desc: 'Panel de control completo desde tu computadora para una administración detallada y profesional.',
      color: 'amber'
    },
    { 
      icon: Zap, 
      title: 'Datos Sincronizados', 
      desc: 'Información de productos y centros de servicio actualizada al instante, sin errores manuales.',
      color: 'teal'
    },
    { 
      icon: Star, 
      title: 'Diseño Profesional', 
      desc: 'Proyecta una imagen de alta credibilidad ante tus prospectos y miembros de tu red con Mi DXN.',
      color: 'rose'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "Aumenta tus Ventas",
      desc: "Presenta productos de forma profesional y cierra más ventas con la información actualizada de Mi DXN",
      color: "emerald"
    },
    {
      icon: Clock,
      title: "Ahorra Tiempo",
      desc: "Automatiza cálculos de PV/SV y verifica DNIs en segundos, no en horas",
      color: "amber"
    },
    {
      icon: Award,
      title: "Alcanza Rangos",
      desc: "Planifica estratégicamente tus compras para mantener y subir de nivel en la red",
      color: "teal"
    }
  ];

  const CurrentIcon = platformSlides[currentSlide].icon;

  const colorClasses = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', light: 'bg-emerald-100', gradient: 'from-emerald-500 to-teal-500' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', light: 'bg-amber-100', gradient: 'from-amber-500 to-orange-500' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', light: 'bg-teal-100', gradient: 'from-teal-500 to-cyan-500' },
    rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', light: 'bg-rose-100', gradient: 'from-rose-500 to-pink-500' }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-emerald-50/30 to-amber-50/30 relative overflow-hidden">
      
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-0 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-emerald-100/10 to-amber-100/10 rounded-full blur-3xl" />
      </div>

      {/* Hero Section Principal */}
      <section className="pt-28 pb-16 lg:pt-32 lg:pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center w-full">
          
          {/* Columna Izquierda - Contenido Enriquecido */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-8 order-2 lg:order-1"
          >
            <div className="inline-flex items-center gap-2 bg-white border border-emerald-200 rounded-full px-4 py-2 shadow-lg shadow-emerald-100/50">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="text-sm font-semibold text-emerald-700">Mi DXN: La Herramienta #1 para Distribuidores</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 leading-tight tracking-tight">
              Impulsa tu Negocio con{' '}
              <span className="bg-gradient-to-r from-emerald-700 via-teal-600 to-emerald-600 bg-clip-text text-transparent">
                Mi DXN
              </span>
            </h1>

            <p className="text-lg text-stone-600 leading-relaxed max-w-lg">
              Olvídate de las hojas de cálculo y la desorganización. La plataforma <strong>Mi DXN</strong> te permite gestionar catálogos actualizados, eventos y cálculos de PV/SV en un solo lugar, tanto en tu móvil como en tu computadora.
            </p>

            {/* Stats en línea */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              {stats.slice(0, 2).map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + idx * 0.1 }}
                    className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-stone-200 shadow-sm"
                  >
                    <Icon className={`w-6 h-6 ${stat.color} mb-2`} />
                    <div className="text-2xl font-bold text-stone-900">{stat.value}</div>
                    <div className="text-xs text-stone-600">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <button className="group bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-8 py-4 rounded-xl font-semibold shadow-xl shadow-emerald-600/20 transition-all flex items-center justify-center gap-2">
                Ver Mi DXN en Acción
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="bg-white hover:bg-stone-50 text-stone-700 border-2 border-stone-200 px-8 py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 shadow-lg">
                <Play className="w-5 h-5 fill-stone-700" />
                Ver Video Demo
              </button>
            </div>

            {/* Trust badges mejorados */}
            <div className="flex flex-wrap items-center gap-6 pt-4 text-sm text-stone-600">
              <div className="flex items-center gap-2 bg-white/60 backdrop-blur-sm px-3 py-2 rounded-lg border border-stone-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="font-medium">Datos de Centros de Servicio</span>
              </div>
              <div className="flex items-center gap-2 bg-white/60 backdrop-blur-sm px-3 py-2 rounded-lg border border-stone-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="font-medium">100% Seguro y Privado</span>
              </div>
            </div>
          </motion.div>

          {/* Columna Derecha - Showcase Enriquecido */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="relative order-1 lg:order-2"
            onMouseEnter={() => setIsPlaying(false)}
            onMouseLeave={() => setIsPlaying(true)}
          >
            {/* Elementos flotantes decorativos */}
            <motion.div 
              animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-6 -left-6 z-20 bg-white rounded-2xl shadow-xl p-4 border border-emerald-100 hidden lg:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">+50%</div>
                  <div className="text-xs text-stone-500">Más Productivo</div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              animate={{ y: [0, 15, 0], rotate: [0, -5, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -bottom-6 -right-6 z-20 bg-white rounded-2xl shadow-xl p-4 border border-amber-100 hidden lg:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">500+</div>
                  <div className="text-xs text-stone-500">Distribuidores</div>
                </div>
              </div>
            </motion.div>

            <div className="relative z-10">
              {/* Círculo decorativo detrás mejorado */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-br from-emerald-200/30 via-amber-200/30 to-teal-200/30 rounded-full blur-3xl" />
              
              {/* Contenedor del Showcase */}
              <div className="relative bg-gradient-to-br from-stone-900 to-stone-800 rounded-3xl shadow-2xl shadow-stone-900/30 overflow-hidden border border-stone-700 w-full h-[450px] lg:h-[550px]">
                
                {/* Barra superior estilo navegador */}
                <div className="h-10 bg-stone-800/90 backdrop-blur flex items-center px-4 gap-2 border-b border-stone-700">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                    <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                  </div>
                  <div className="ml-4 flex-1 h-6 bg-stone-900/60 rounded-md text-[10px] text-stone-400 flex items-center px-3">
                    <Globe className="w-3 h-3 mr-2" />
                    app.midxn.com/dashboard
                  </div>
                </div>

                {/* Área de imagen */}
                <div className="relative w-full h-full">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentSlide}
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                      className="absolute inset-0 p-4"
                    >
                      <div className="w-full h-full bg-stone-100 rounded-xl overflow-hidden border border-stone-600">
                        <img 
                          src={platformSlides[currentSlide].url}
                          alt={platformSlides[currentSlide].title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/20 to-transparent rounded-xl" />
                      
                      {/* Contenido sobre la imagen */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={`text-${currentSlide}`}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.4 }}
                            className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20"
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <div className={`p-2 rounded-lg ${platformSlides[currentSlide].badgeColor}`}>
                                <CurrentIcon className="w-4 h-4 text-white" />
                              </div>
                              <span className="font-bold text-white">{platformSlides[currentSlide].title}</span>
                            </div>
                            <p className="text-sm text-stone-200">
                              {platformSlides[currentSlide].desc}
                            </p>
                          </motion.div>
                        </AnimatePresence>
                      </div>

                      {/* Badge flotante */}
                      <motion.div
                        key={`badge-${currentSlide}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className={`absolute top-4 right-4 ${platformSlides[currentSlide].badgeColor} text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1.5`}
                      >
                        <Zap className="w-3 h-3" />
                        {platformSlides[currentSlide].badge}
                      </motion.div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* Dots de navegación mejorados */}
              <div className="flex justify-center gap-3 mt-6">
                {platformSlides.map((slide, idx) => {
                  const Icon = slide.icon;
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        setCurrentSlide(idx);
                        setIsPlaying(false);
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                        currentSlide === idx 
                          ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30' 
                          : 'bg-white/60 text-stone-600 hover:bg-white'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-xs font-medium hidden sm:inline">{slide.badge}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats Grid - Sección adicional */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6 mt-16 lg:mt-24"
        >
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 + idx * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-stone-200 shadow-lg hover:shadow-xl transition-all text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl mb-3">
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="text-3xl font-bold text-stone-900 mb-1">{stat.value}</div>
                <div className="text-sm text-stone-600 font-medium">{stat.label}</div>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* Sección de Beneficios */}
      <section className="bg-white py-20 border-y border-stone-200 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-stone-900 mb-4">
              ¿Por qué elegir Mi DXN?
            </h3>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Herramientas diseñadas específicamente para potenciar tu negocio de manera inteligente
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              const colors = colorClasses[benefit.color];
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -8 }}
                  className={`group ${colors.bg} rounded-2xl p-8 border ${colors.border} hover:shadow-2xl transition-all duration-300`}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${colors.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-stone-900 mb-3">{benefit.title}</h4>
                  <p className="text-stone-600 leading-relaxed">{benefit.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sección de Características */}
      <section id="funciones" className="bg-gradient-to-br from-stone-50 to-emerald-50/30 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-white border border-emerald-200 rounded-full px-4 py-2 mb-6 shadow-sm"
            >
              <Smartphone className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">Tecnología de Vanguardia</span>
            </motion.div>
            
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-stone-900 mb-6"
            >
              Todo lo que Necesitas en un Solo Lugar
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed"
            >
              Diseñado específicamente para las necesidades reales de los distribuidores, 
              combinando la esencia del bienestar con la eficiencia digital de Mi DXN.
            </motion.p>
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              const colors = colorClasses[feature.color];
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -8 }}
                  className={`group bg-white rounded-2xl p-6 border ${colors.border} hover:shadow-2xl hover:shadow-stone-900/5 transition-all duration-300`}
                >
                  <div className={`w-14 h-14 ${colors.light} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-7 h-7 ${colors.text}`} />
                  </div>
                  <h3 className="font-bold text-stone-900 text-lg mb-3 group-hover:text-emerald-700 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA Final Mejorado */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="bg-gradient-to-br from-emerald-800 via-teal-800 to-emerald-900 rounded-3xl p-8 md:p-16 text-center text-white relative overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/5 to-transparent" />
          
          {/* Elementos decorativos */}
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl" />
          
          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">¿Listo para potenciar tu red con Mi DXN?</h2>
              <p className="text-emerald-100 mb-10 max-w-2xl mx-auto text-lg leading-relaxed">
                Únete a los distribuidores que ya están gestionando su negocio de forma inteligente, 
                ahorrando tiempo y multiplicando sus resultados.
              </p>


              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://wa.me/51968039152?text=Hola,%20quiero%20comenzar%20con%20Mi%20DXN"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-emerald-900 px-10 py-5 rounded-xl font-bold hover:bg-emerald-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 inline-flex items-center justify-center gap-2"
                >
                  Comenzar con Mi DXN
                  <ArrowRight className="w-5 h-5" />
                </a>
                
                <a 
                  href="https://wa.me/51968039152?text=Hola,%20quisiera%20más%20información%20sobre%20Mi%20DXN"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-emerald-700/50 backdrop-blur text-white border-2 border-emerald-400/30 px-10 py-5 rounded-xl font-bold hover:bg-emerald-700/70 transition-all hover:scale-105 inline-flex items-center justify-center gap-2"
                >
                  {/* Asegúrate de tener MessageCircle importado de 'lucide-react' en este archivo */}
                  <MessageCircle className="w-5 h-5" />
                  Contactar por WhatsApp
                </a>
              </div>
              
              <p className="mt-8 text-sm text-emerald-200/80">
                ✓ Sin compromiso  ✓ Configuración gratuita  ✓ Soporte 24/7
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hero;